/**
 * providerScoring.js
 *
 * Scores a single provider row against a TriageOutput + userContext.
 * Returns a numeric score 0–100. Higher is better.
 *
 * Weights (must sum to 100):
 *   30 — site-of-care fit       (does the provider category match suggestedCareSites?)
 *   20 — insurance match        (does the provider accept the user's carrier?)
 *   20 — availability signals   (open now, same-day)
 *   10 — age fit                (pediatrician for child, etc.)
 *   10 — distance               (closer is better; linear decay over 25 miles)
 *   10 — capability signals     (telehealth, accepting new patients)
 */

// Map triage suggestedCareSites → provider category values
const SITE_TO_CATEGORY = {
  er: ['er', 'emergency'],
  urgent_care: ['urgent_care', 'urgent care'],
  primary_care: ['primary', 'primary_care', 'family medicine', 'internal medicine'],
  pediatrician: ['pediatric', 'pediatrics'],
  telehealth: ['telehealth'],
  orthopedic_urgent_care: ['orthopedic', 'orthopedics', 'orthopedic urgent care'],
};

/**
 * Normalize a provider category string for comparison.
 * @param {string} cat
 * @returns {string}
 */
function normalizeCategory(cat) {
  return (cat || '').toLowerCase().trim();
}

/**
 * Compute the site-of-care fit score (0–30).
 * @param {object} provider
 * @param {string[]} suggestedCareSites
 * @returns {number}
 */
function scoreSiteOfCare(provider, suggestedCareSites) {
  if (!suggestedCareSites || suggestedCareSites.length === 0) return 15; // neutral

  const providerCat = normalizeCategory(provider.category);

  for (let i = 0; i < suggestedCareSites.length; i++) {
    const site = suggestedCareSites[i];
    const acceptedCats = SITE_TO_CATEGORY[site] || [];
    if (acceptedCats.some(c => providerCat.includes(c))) {
      // First match gets full 30, subsequent matches get slightly less
      return i === 0 ? 30 : 20;
    }
  }

  return 0;
}

/**
 * Compute insurance match score (0–20).
 * @param {object} provider  — provider row with `insurance` (string or array)
 * @param {string} carrier   — userStore.carrier (e.g. 'bcbs')
 * @returns {number}
 */
function scoreInsurance(provider, carrier) {
  if (!carrier) return 10; // unknown — neutral

  const insurance = provider.insurance;
  if (!insurance) return 0;

  const carrierLower = carrier.toLowerCase();
  const insuranceLower =
    typeof insurance === 'string'
      ? insurance.toLowerCase()
      : JSON.stringify(insurance).toLowerCase();

  // Partial match on carrier abbreviations
  const aliases = {
    bcbs: ['bluecross', 'blue cross', 'bcbs'],
    aetna: ['aetna'],
    medicaid: ['medicaid', 'sc medicaid', 'healthy connections'],
    medicare: ['medicare'],
    cigna: ['cigna'],
    united: ['united', 'unitedhealthcare', 'uhc'],
    humana: ['humana'],
  };

  const terms = aliases[carrierLower] || [carrierLower];
  if (terms.some(t => insuranceLower.includes(t))) return 20;

  return 0;
}

/**
 * Compute availability score (0–20).
 * @param {object} provider
 * @returns {number}
 */
function scoreAvailability(provider) {
  let score = 0;
  if (provider.same_day) score += 12;
  if (provider.status === 'accepting') score += 8;
  else if (provider.status === 'waitlist') score += 3;
  return Math.min(score, 20);
}

/**
 * Compute age-fit score (0–10).
 * @param {object} provider
 * @param {string} ageBand — 'infant' | 'child' | 'teen' | 'adult' | 'older_adult'
 * @returns {number}
 */
function scoreAgeFit(provider, ageBand) {
  if (!ageBand) return 5; // neutral

  const cat = normalizeCategory(provider.category);
  const isPediatric = cat.includes('pediatric');

  if (['infant', 'child', 'teen'].includes(ageBand)) {
    return isPediatric ? 10 : 5;
  }
  // Adults don't get penalized for seeing a general practice
  return isPediatric ? 2 : 7;
}

/**
 * Compute distance score (0–10).
 * Linear decay: 10 at 0 miles, 0 at ≥25 miles.
 * @param {object} provider  — provider row with `lat`, `lng`
 * @param {number|null} userLat
 * @param {number|null} userLng
 * @returns {number}
 */
function scoreDistance(provider, userLat, userLng) {
  if (userLat == null || userLng == null) return 5; // unknown — neutral
  if (provider.lat == null || provider.lng == null) return 3; // no coords

  const dist = haversineDistanceMiles(userLat, userLng, provider.lat, provider.lng);
  if (dist >= 25) return 0;
  return Math.round(10 * (1 - dist / 25));
}

/**
 * Compute capability score (0–10).
 * @param {object} provider
 * @returns {number}
 */
function scoreCapability(provider) {
  let score = 0;
  if (provider.telehealth) score += 4;
  if (provider.featured) score += 4;
  if (provider.book_url) score += 2;
  return Math.min(score, 10);
}

/**
 * Haversine formula — approximate miles between two lat/lng pairs.
 */
function haversineDistanceMiles(lat1, lng1, lat2, lng2) {
  const R = 3958.8; // Earth radius in miles
  const dLat = deg2rad(lat2 - lat1);
  const dLng = deg2rad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(deg2rad(lat1)) *
      Math.cos(deg2rad(lat2)) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function deg2rad(deg) {
  return deg * (Math.PI / 180);
}

/**
 * Score a single provider.
 *
 * @param {object} provider         — Supabase provider row
 * @param {object} triageOutput     — result of triageEngine.evaluate()
 * @param {object} userContext      — { carrier, ageBand, userLat, userLng }
 * @returns {number}                — 0–100
 */
export function scoreProvider(provider, triageOutput, userContext = {}) {
  const { carrier, ageBand, userLat, userLng } = userContext;

  return (
    scoreSiteOfCare(provider, triageOutput.suggestedCareSites) +
    scoreInsurance(provider, carrier) +
    scoreAvailability(provider) +
    scoreAgeFit(provider, ageBand) +
    scoreDistance(provider, userLat, userLng) +
    scoreCapability(provider)
  );
}
